import { Component, effect, ElementRef, inject, signal, viewChild, ViewChild } from '@angular/core';
import { AccountSettingsDialogService } from '../../services/account-settings-dialog.service';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';
import { AccountSettingsComponent } from '../../pages/account-settings/account-settings.component';

type SettingsTab = 'profile' | 'preferences' | 'appearance';

/** PAT-203 — TRIARQ-style settings modal with left nav (QPrime blue accents). */
@Component({
  selector: 'app-account-settings-dialog',
  standalone: true,
  imports: [AccountSettingsComponent],
  templateUrl: './account-settings-dialog.component.html',
  styleUrl: './account-settings-dialog.component.scss',
})
export class AccountSettingsDialogComponent {
  readonly dialog = inject(AccountSettingsDialogService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  @ViewChild(AccountSettingsComponent) private settingsRef?: AccountSettingsComponent;
  private readonly scrollPanel = viewChild<ElementRef<HTMLElement>>('scrollPanel');

  activeTab = signal<SettingsTab>('profile');

  readonly tabs: { id: SettingsTab; icon: string; label: string }[] = [
    { id: 'profile', icon: 'fa-solid fa-user', label: 'Profile' },
    { id: 'appearance', icon: 'fa-solid fa-circle-half-stroke', label: 'Appearance' },
    { id: 'preferences', icon: 'fa-solid fa-sliders', label: 'Preferences' },
  ];

  constructor() {
    effect(() => {
      this.activeTab();
      queueMicrotask(() => {
        this.scrollPanel()?.nativeElement.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      });
    });
  }

  async onBackdropClick(): Promise<void> {
    await this.requestClose();
  }

  async requestClose(): Promise<void> {
    if (!(await this.confirmDiscardIfDirty())) return;
    this.settingsRef?.revertUnsavedChanges();
    this.dialog.close();
    this.activeTab.set('profile');
  }

  async onSave(): Promise<void> {
    if (!this.settingsRef) return;
    await this.settingsRef.save();
    if (!this.settingsRef.saveError()) {
      this.dialog.close();
      this.activeTab.set('profile');
    }
  }

  private async confirmDiscardIfDirty(): Promise<boolean> {
    if (!this.dialog.hasUnsavedChanges()) return true;
    return this.confirmDialog.confirm({
      title: 'Unsaved changes',
      message: 'You have unsaved changes. Discard them?',
      confirmLabel: 'Discard',
      cancelLabel: 'Stay',
      destructive: true,
    });
  }
}
